/**
 * Mail on `quoflow.quoflow.quoflow.api.mail.*` — the method namespace this migration moves onto
 * (see `bosun.ts`'s header). Task 6 of `docs/superpowers/plans/2026-08-04-bosun-api-migration.md`;
 * live shapes verified against rfqdev.etoserp.com, 2026-08-04.
 *
 * Follows the pattern `useItems.ts` set in Task 5: a `Wire*` interface next to each hook, a pure
 * exported `toX` converter, no shared mapper file. Eight methods have a hook here:
 * `get_emails`, `get_email`, `send_email`, `mark_read`, `flag_email`, `delete_email`,
 * `get_mailbox_info`, `extract_enquiry_items`. The seventh (`extract_enquiry_items`) is called
 * directly from `useMailExtraction.ts`, not wrapped here — see that file. A ninth method exists
 * on the wire (`create_enquiry_from_mail`, the one-shot extract-and-create) but has no hook here
 * any more — see this file's own comment where that hook used to live (fix round, final
 * whole-branch review, finding 6) for why. "Create enquiry from this mail" (`CreateEnquiryBar.tsx`)
 * is a DIFFERENT flow — extract, let the purchaser confirm/edit the guessed customer and delivery
 * location, then create — and goes through
 * `useCreateEnquiry` in `useEnquiryMutations.ts` (`enquiry.create_enquiry`), not this method; see
 * that hook's own header for a later bug fix in the same area (it used to write the wrong
 * doctype).
 *
 * ---- What changed from the previous pass, and why ----
 *
 * The previous transport (`quoflow.api.get_email_communications` / `/api/resource/Communication`,
 * both gone now) had NO server-side folder, no persisted read/flag state and no delete. Every
 * "folder" the screen showed was a client-side `select` over one whole-mailbox fetch, and
 * flag/archive/delete were mock-only writes that could never agree with a live-sourced id — see
 * the git history of this file for that whole saga.
 *
 * `get_emails` takes a REAL `folder` param and `mark_read`/`flag_email`/`delete_email` are real
 * writes, so all of that client-side derivation is gone: `useMailbox(folder)` asks the server for
 * exactly that folder, and a mark-read/delete call actually changes what the next fetch returns.
 *
 * ---- Defect B4: `bosun_role` is null on every account ----
 *
 * `get_mailbox_info` exists to say which Email Account is the customer side and which is the
 * supplier side, and on the live bench it answers `bosun_role: null` for both. There is no honest
 * way to tell them apart from that response. Two consequences, both deliberate:
 *
 * 1. `toMailboxInfo` DROPS an account with no role rather than guessing one — `accounts` comes
 *    back `[]` today, and the Mail screen's account switcher (`FolderRail`) renders disabled with
 *    a tooltip explaining why, never a silently-wrong customer/supplier label. See its own test.
 * 2. Every `MailMessage.account` this file produces is the fixed placeholder `'customer'` — see
 *    `toMailListItem`'s comment. `MailMessage.account` (`src/lib/types.ts`) is a non-nullable
 *    `MailAccount`, and this task does not own that file, so there is no `null`/`'unknown'` value
 *    to fall back to. Nothing downstream keys real behaviour off it any more: the folder rail's
 *    account grouping was already dead (every `MailFolderSummary` carries `account: null`, as
 *    before), and the disabled switcher above is what B4 actually asks for.
 *
 * ---- Dropped: Archive, Save draft ----
 *
 * Neither has a method in this task's list. `archive_email` never existed on any transport this
 * screen used; `mail.send_email` is the only send/compose write, and nothing resembling
 * "save without sending" is in the nine methods. Both actions are removed from the Mail screen's
 * components (`ReaderPane`, `ComposeModal`) rather than left pointing at a mock-only stub that a
 * live run could never call. Drafts is still a real folder to READ (`get_emails?folder=Drafts`) —
 * whatever the account's own mail client has genuinely saved as a draft — just not one Bosun can
 * write to.
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fileHref } from './fileUrl'
import { callDelete, callGet, callPost } from './bosun'
import { bool01, num, str } from './wire'
import { BosunApiError } from './errors'
import { toUploadBody, toUploadedFile, withFileDocname, type WireUploadAttachment } from '../mailUpload'
import { getResource } from './frappe'
import { plainPreview } from '../mail'
import { qk } from './keys'
import { dedupeById, nextMailOffset } from '../mailPaging'
import { POLL_MS } from './polling'
import type { ComposePayload, MailAttachment, MailFolder, MailMessage, UploadedFile } from '../types'

/**
 * The guarantee, not the optimisation. `useRealtime.ts` (mounted once in `Layout.tsx`)
 * invalidates `qk.mailRoot()` the instant a `mail.new` event arrives, but realtime is live-only
 * today — the mock runs no socket server, see that file's header — and even against a real
 * server, a dropped or misconfigured socket must not mean mail stops refreshing: a missed
 * customer enquiry costs far more than a request every 30s. Poll ALWAYS; let realtime shorten
 * the wait on top of it, never replace it. Was on this file pre-migration (`MAIL_POLL_MS`,
 * `git show 1712e74:src/lib/api/useMail.ts`) and was silently dropped in the Task 6 rewrite —
 * restored here at the same 30s.
 */
export const MAIL_POLL_MS = POLL_MS

/* ============================================================================================
 * mail.get_emails → useMailbox
 * ============================================================================================ */

/** One row of `mail.get_emails`. `recipients`/`cc` are comma-joined strings, or `null` — never
 * arrays, unlike the compose payload this screen sends. */
export interface WireMailListItem {
  name: string
  subject: string
  sender: string
  sender_full_name: string
  recipients: string | null
  cc: string | null
  sent_or_received: string
  status: string
  seen: number | boolean
  email_account: string
  communication_date: string
  content: string
}

/**
 * `total`/`limit`/`offset` are OPTIONAL on the live response — verified, not a guess.
 *
 * `folder_counts` is the one the mail rail actually needs: `{"Inbox": {"unread": 0},
 * "Sent": {"unread": 22}}`, real per-folder unread counts computed server-side, independent of
 * how many rows this particular request asked for. Without it the rail showed the fetch LIMIT as
 * its badge — every unopened folder read "1" because that is what it was fetched with.
 *
 * Keys are the SERVER's folder names (`Inbox`, `Sent`, …), not our lower-case `MailFolder`.
 */
export interface WireMailboxPage {
  data: WireMailListItem[]
  total?: number
  folder_counts?: Record<string, { unread?: number } | undefined>
  limit?: number
  offset?: number
}

/** Only these four are live-verified folder values `get_emails` accepts; `flagged`/`archive`
 * (part of the wider `MailFolder` union) have no server-side folder to key off and nothing in
 * this screen calls this hook with either — see the module header, "Dropped". */
const WIRE_FOLDER: Partial<Record<MailFolder, string>> = {
  inbox: 'Inbox',
  sent: 'Sent',
  drafts: 'Drafts',
  deleted: 'Deleted',
}

export function toWireFolder(folder: MailFolder): string {
  return WIRE_FOLDER[folder] ?? folder.charAt(0).toUpperCase() + folder.slice(1)
}

/**
 * `"a@x, Name <b@y>"` → `['a@x', 'b@y']` — the bare addresses. The Cc convention (the purchaser
 * copied on every enquiry) is matched against user emails, so a display name must not stay
 * glued to the address (2026-09-21).
 */
function toAddressList(value: string | null | undefined): string[] {
  if (!value || value === 'null') return []
  return value
    .split(',')
    .map((entry) => {
      const angled = /<([^>]+)>/.exec(entry)
      return (angled ? angled[1] : entry).trim()
    })
    .filter((address) => address !== '')
}

/** One `get_emails` row → `MailMessage`. `folder` is the one it was fetched with — the server
 * already filtered to it, so there is nothing to derive. */
export function toMailListItem(w: WireMailListItem, folder: MailFolder): MailMessage {
  const content = str(w.content)
  const senderFullName = str(w.sender_full_name)
  return {
    id: str(w.name),
    // Defect B4 — see the module header. Fixed placeholder; nothing reads this for real
    // customer/supplier behaviour any more.
    account: 'customer',
    folder,
    from: str(w.sender),
    from_name: senderFullName !== '' ? senderFullName : str(w.sender),
    to: toAddressList(w.recipients),
    cc: toAddressList(w.cc),
    subject: str(w.subject),
    preview: plainPreview(content),
    body_html: content,
    received_at: str(w.communication_date),
    unread: !bool01(w.seen),
    // `get_emails` carries no flag field at all, even though `flag_email` is a real write below —
    // a flagged message therefore reads as unflagged again on the next fetch. Documented gap, not
    // a mapping bug: there is nothing here to read it back from.
    flagged: false,
    // No field links a message to an Opportunity in this response — always null, not guessed from
    // the subject text (the old `[REQ-####]` tag is gone; the current naming series is `OPP-…`).
    enquiry_id: null,
    // Attachments are not on the list row — only `get_email` (below) returns them. Same reasoning
    // `items.get_items` uses for its own per-row lookups: a folder can run to hundreds of rows, so
    // fetching each one's attachments here would be an N+1, not a list.
    attachments: [],
  }
}

/**
 * The rows a folder shows, derived from the two folders the server actually keeps.
 *
 * Measured 2026-09-16: `get_emails` honours `Inbox` and `Sent` only — `Drafts`, `Deleted`, `Trash`
 * and anything else return the INBOX, which is why three rails showed one list. And
 * `delete_email` marks a Communication `status: "Archived"` (answering `folder: "deleted"`) but
 * leaves it in its Inbox/Sent list. So: Inbox and Sent are their un-archived rows, Deleted is the
 * archived rows of both, and Drafts is empty — the server keeps none (`send_email` sends at once).
 * Filed as `D38`; the day the server honours the folders, this reduces to a pass-through.
 */
export function folderRows<T extends { status: string }>(folder: MailFolder, inbox: readonly T[], sent: readonly T[]): T[] {
  const archived = (r: T) => r.status === 'Archived'
  switch (folder) {
    case 'inbox':
      return inbox.filter((r) => !archived(r))
    case 'sent':
      return sent.filter((r) => !archived(r))
    case 'deleted':
      return [...inbox.filter(archived), ...sent.filter(archived)]
    case 'drafts':
      return []
    default:
      return inbox.filter((r) => !archived(r))
  }
}

async function fetchFolder(folder: 'Inbox' | 'Sent', limit: number, offset = 0): Promise<WireMailboxPage> {
  return callGet<WireMailboxPage>('mail', 'get_emails', { folder, limit, offset })
}

/**
 * One page of a folder — the shape every page of the infinite query carries.
 *
 * `rows` is what came back from the SERVER, before `folderRows` drops archived or non-matching
 * rows: the paging rule needs the server's own count to tell a full page from the last one
 * (`mailPaging.ts`), and a filtered count would end the list early on a page that happened to
 * be all archived mail.
 */
interface MailboxPage {
  data: MailMessage[]
  rows: number
  folderCounts: WireMailboxPage['folder_counts']
}

/**
 * A folder, a page at a time (Akhil, 2026-09-25).
 *
 * `get_emails` caps a page at 100 rows and its `total` only echoes what it returned, so the list
 * grows by fetching the next offset until a short page says that is all — see `mailPaging.ts`.
 * `MessageList` asks for the next page when the bottom of the list comes into view.
 *
 * Inbox and Sent page against the server. Deleted is derived from BOTH (an archived mail may be
 * either) and Drafts from neither, so those stay a single page — they are small, and there is no
 * server-side folder to offset into.
 */
export function useMailbox(folder: MailFolder, limit = 200) {
  const query = useInfiniteQuery({
    queryKey: qk.mailbox(folder, limit),
    initialPageParam: 0,
    getNextPageParam: (_last: MailboxPage, pages: MailboxPage[]) =>
      folder === 'inbox' || folder === 'sent' ? nextMailOffset(pages.map((p) => ({ rows: p.rows }))) : null,
    queryFn: async ({ pageParam }): Promise<MailboxPage> => {
      const [inboxPage, sentPage] =
        folder === 'inbox'
          ? [await fetchFolder('Inbox', limit, pageParam), null]
          : folder === 'sent'
            ? [null, await fetchFolder('Sent', limit, pageParam)]
            : folder === 'deleted'
              ? await Promise.all([fetchFolder('Inbox', limit), fetchFolder('Sent', limit)])
              : [null, null]
      const raw = folder === 'sent' ? (sentPage?.data ?? []) : (inboxPage?.data ?? [])
      const rows = folderRows(folder, inboxPage?.data ?? [], sentPage?.data ?? [])
      return {
        data: rows.map((row) => toMailListItem(row, folder)),
        // The SERVER's row count for this page — see `MailboxPage`.
        rows: folder === 'deleted' ? 0 : raw.length,
        // `folder_counts` is keyed by the server's own folder names, so it is passed through
        // untouched and read by key at the call site (`MailPage`'s rail).
        folderCounts: inboxPage?.folder_counts ?? sentPage?.folder_counts ?? {},
      }
    },
    // The poll re-reads every page that is loaded, which is the price of keeping a list that has
    // been paged through fresh — and the reason the page size is the server's maximum.
    refetchInterval: MAIL_POLL_MS,
  })

  const pages = query.data?.pages ?? []
  // One row per mail: a page fetched after new mail arrived can repeat the row above it.
  const data = dedupeById(pages.flatMap((p) => p.data))
  return {
    ...query,
    data: query.data
      ? {
          data,
          // What is ON SCREEN. The server offers no grand total (its `total` echoes the page), so
          // claiming one would be an invention — the rail shows what has been loaded.
          total: data.length,
          folderCounts: pages[0]?.folderCounts ?? {},
        }
      : undefined,
  }
}

/* ============================================================================================
 * mail.get_email → useMailMessage
 * ============================================================================================ */

export interface WireMailAttachment {
  /** The `File` DOCNAME — what `send_email` looks the attachment up by. Measured on `get_email`
   *  2026-09-22; the August note that this response carried no id was stale. */
  name?: string
  file_name: string
  file_url: string
  file_size: number
  file_type: string
  is_private: number | boolean
}

export interface WireMailDetail {
  name: string
  subject: string
  sender: string
  sent_or_received: string
  content: string
  unread: number | boolean
  attachments: WireMailAttachment[]
  /** Present since 2026-09-21 (measured) — the August note that the detail carried none was stale. */
  recipients?: string | null
  cc?: string | null
  sender_full_name?: string | null
  communication_date?: string | null
}

const EXTENSION_KIND: Record<string, MailAttachment['kind']> = {
  xlsx: 'xlsx',
  xls: 'xlsx',
  /*
    Macro-enabled and binary workbooks. `.xlsm` is the SAME OOXML zip as `.xlsx` — the macros do
    not change the container — and `read-excel-file`, the parser this app actually uses, opens one
    without complaint (verified 2026-09-08 on `RFQ_RFQPRPV-008726…xlsm`: a 563-row `Quote` sheet).

    Without these it fell all the way through to `'file'` and was reported as "not a file type we
    read", because the MIME fallback below had nothing to work with either — Frappe stored that
    File row with `file_type: null`. A real customer RFQ, skipped for the sake of one letter.
  */
  xlsm: 'xlsx',
  xlsb: 'xlsx',
  pdf: 'pdf',
  doc: 'doc',
  docx: 'doc',
  png: 'img',
  jpg: 'img',
  jpeg: 'img',
  // Outlook's own export format for a pasted image. Real on this bench ("whip check .jfif"), and
  // without it a photograph was classified `file` and reported as "not a file type we read"
  // instead of "needs a vision model" — the wrong answer to the wrong question.
  jfif: 'img',
  gif: 'img',
  tif: 'img',
  tiff: 'img',
  heic: 'img',
  webp: 'img',
  bmp: 'img',
  svg: 'img',
}

export function toAttachmentKind(fileName: string, fileType: string): MailAttachment['kind'] {
  const extension = (fileName.split('.').pop() ?? '').toLowerCase()
  if (EXTENSION_KIND[extension]) return EXTENSION_KIND[extension]
  const type = fileType.toLowerCase()
  if (type.includes('pdf')) return 'pdf'
  if (type.includes('sheet') || type.includes('excel')) return 'xlsx'
  if (type.includes('word') || type.includes('msword')) return 'doc'
  if (type.includes('image')) return 'img'
  return 'file'
}

/** Display only — the raw byte count is never shown. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`
  const mb = kb / 1024
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`
}

export function toMailAttachment(w: WireMailAttachment): MailAttachment {
  const fileName = str(w.file_name)
  const fileUrl = str(w.file_url)
  const bytes = num(w.file_size)
  return {
    /*
      The `File` DOCNAME, because that is what a SEND does with it: `mail.send_email` runs
      `get_doc("File", <attachment>)`, and a url gets `DoesNotExistError: File {'name':
      '/private/files/…'} not found` — a 500 on every forward (2026-09-22). The url and the
      name remain as fallbacks for a wire that carries no docname.
    */
    id: str(w.name) || fileUrl || fileName,
    name: fileName,
    kind: toAttachmentKind(fileName, str(w.file_type)),
    size: bytes > 0 ? formatBytes(bytes) : '',
    // NOT `apiBase() + file_url`: nginx serves `/private/files` itself and Frappe's CORS headers
    // are absent from that response, so a cross-origin fetch of it succeeds and is then unreadable
    // — the defect that broke every xlsx/pdf preview in the Azure deploy. `fileHref` routes the
    // same bytes through the permission-checked `download_file` method, which does carry them.
    // See `./fileUrl.ts` for the measurements.
    preview_url: fileHref(fileUrl),
  }
}

function toMailDetailFolder(sentOrReceived: string): MailFolder {
  return sentOrReceived === 'Sent' ? 'sent' : 'inbox'
}

/** `get_email`'s shape is narrower than `get_emails`'s list row — no `sender_full_name`,
 * `recipients`, `cc`, `email_account` or `communication_date`. Each gap is left honestly blank
 * (or, for `from_name`, falls back to the address) rather than guessed. */
export function toMailDetail(w: WireMailDetail): MailMessage {
  const content = str(w.content)
  return {
    id: str(w.name),
    account: 'customer', // Defect B4 — see the module header.
    folder: toMailDetailFolder(str(w.sent_or_received)),
    from: str(w.sender),
    from_name: str(w.sender_full_name) || str(w.sender),
    // Carried by the detail since 2026-09-21 (measured) — the purchaser on Cc is read from here.
    to: toAddressList(w.recipients),
    cc: toAddressList(w.cc),
    subject: str(w.subject),
    preview: plainPreview(content),
    body_html: content,
    received_at: str(w.communication_date),
    unread: bool01(w.unread),
    flagged: false, // no flag field — see toMailListItem's comment
    enquiry_id: null,
    attachments: (w.attachments ?? []).map(toMailAttachment),
  }
}

export function useMailMessage(id: string | null) {
  return useQuery({
    queryKey: qk.mailMessage(id ?? ''),
    queryFn: async () => toMailDetail(await callGet<WireMailDetail>('mail', 'get_email', { name: id })),
    enabled: !!id,
    refetchInterval: MAIL_POLL_MS,
  })
}

/* ============================================================================================
 * mail.get_mailbox_info → useMailboxInfo
 * ============================================================================================ */

export interface WireMailboxAccount {
  name: string
  email_id: string
  enable_incoming?: number | boolean
  enable_outgoing?: number | boolean
  default_incoming?: number | boolean
  default_outgoing?: number | boolean
  bosun_role: string | null
}

export interface WireMailboxInfo {
  email_accounts: WireMailboxAccount[]
  account_count: number
  frappe_realtime_events: { event: string; when: string }[]
}

/** One account the switcher COULD offer — only ever populated when the server has actually said
 * which side (customer/supplier) it is. */
export interface MailAccountInfo {
  name: string
  email_id: string
  role: string
}

export interface MailboxInfo {
  accounts: MailAccountInfo[]
  realtimeEvents: string[]
}

/**
 * Defect B4: `bosun_role` is `null` on every account on the live bench. An account with no role
 * is DROPPED here, not defaulted to a guessed side — the switcher (`FolderRail`) renders disabled
 * with a tooltip when `accounts` is empty, rather than picking an assignment nothing confirms.
 */
export function toMailboxInfo(w: WireMailboxInfo): MailboxInfo {
  return {
    accounts: w.email_accounts
      .filter((a): a is WireMailboxAccount & { bosun_role: string } => !!a.bosun_role && a.bosun_role.trim() !== '')
      .map((a) => ({ name: a.name, email_id: a.email_id, role: a.bosun_role })),
    realtimeEvents: (w.frappe_realtime_events ?? []).map((e) => e.event),
  }
}

export function useMailboxInfo() {
  return useQuery({
    queryKey: qk.mailboxInfo(),
    queryFn: async () => toMailboxInfo(await callGet<WireMailboxInfo>('mail', 'get_mailbox_info')),
    // Which accounts exist barely changes within a session.
    staleTime: 5 * 60_000,
  })
}

/* ============================================================================================
 * mutations: mark_read, flag_email, delete_email, send_email
 * ============================================================================================ */

interface MutationOptions<TData> {
  onSuccess?: (data: TData) => void
  onError?: (error: BosunApiError) => void
}

/** Every mail write invalidates the whole `['mail', …]` cache — a read/flag/delete/send all move
 * a message between folders or change what a folder's next fetch returns. */
function useMailMutation<TData, TVars>(mutationFn: (vars: TVars) => Promise<TData>, options?: MutationOptions<TData>) {
  const queryClient = useQueryClient()
  return useMutation<TData, BosunApiError, TVars>({
    mutationFn,
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: qk.mailRoot() })
      options?.onSuccess?.(data)
    },
    onError: (error) => options?.onError?.(error),
  })
}

export const useMarkRead = (options?: MutationOptions<void>) =>
  useMailMutation<void, { id: string; read: boolean }>(async ({ id, read }) => {
    await callPost('mail', 'mark_read', { name: id, read })
  }, options)

/**
 * `flag_email` takes an explicit `flagged` boolean, not a toggle — and since `get_emails`/
 * `get_email` never return a flag field to read back (see `toMailListItem`), the caller can never
 * honestly know the CURRENT state to invert. Every call site therefore sends `flagged: true`; see
 * `MailPage`.
 */
export const useFlagMail = (options?: MutationOptions<void>) =>
  useMailMutation<void, { id: string; flagged: boolean }>(async ({ id, flagged }) => {
    await callPost('mail', 'flag_email', { name: id, flagged })
  }, options)

export const useDeleteMail = (options?: MutationOptions<void>) =>
  useMailMutation<void, string>(async (id) => {
    await callDelete('mail', 'delete_email', { name: id })
  }, options)

interface WireSendEmailResult {
  name?: string
}

/**
 * `mail.send_email` is the only send-side write in this task's list — no draft, no archive (see
 * the module header). `payload.account` is Bosun's own `'customer' | 'supplier'` placeholder
 * (`ComposePayload`, `src/lib/types.ts`); B4 blocks resolving it to a real Email Account name, and
 * there is nothing truer to send in its place, so it goes over the wire as-is.
 */
export function useSendMail(options?: { onSuccess?: (id: string) => void; onError?: (e: BosunApiError) => void }) {
  return useMailMutation<string, ComposePayload>(async (payload) => {
    const result = await callPost<WireSendEmailResult>('mail', 'send_email', {
      account: payload.account,
      to: payload.to,
      cc: payload.cc ?? [],
      subject: payload.subject,
      body_html: payload.body_html,
      attachments: payload.attachments ?? [],
    })
    return str(result?.name)
  }, options)
}

/* ============================================================================================
 * mail.create_enquiry_from_mail — NOT wrapped by a hook here; see below
 * ============================================================================================
 *
 * Fix round (final whole-branch review, finding 6): this file used to export a SECOND
 * `useCreateEnquiryFromMail`, colliding in name (never in the compiler, since no single file
 * imported both) with `src/lib/api/useEnquiryFromMail.ts`'s hook of the same name (since deleted
 * in a later bug fix — see that file's own header). The two had genuinely different jobs (this one
 * wrapped the one-shot `mail.create_enquiry_from_mail`, which extracts AND creates server-side in
 * a single call; the other posted a manually-filled form straight to `/api/resource/Opportunity`),
 * but they diverged on `onSuccess` invalidation: this one invalidated only `['mail']`, the other
 * `['enquiries']` AND `['mail']`. Since this one had zero callers, creating an enquiry through
 * IT (were anything ever wired to it) would leave the Kanban (`['enquiries']`) stale — a bug that
 * would only surface the moment something started using it. Deleted rather than fixed in place:
 * a real caller for the one-shot extraction flow can re-add a properly-invalidating hook when
 * `CreateEnquiryBar` (or a successor) actually needs it, rather than this file speculatively
 * maintaining a second implementation of the same name nothing calls.
 * ============================================================================================ */

/* ============================================================================================
 * mail.upload_attachment → useUploadFile   (ask `A4`, CLOSED 2026-09-09)
 * ============================================================================================
 *
 * This used to POST a `FormData` to the mock-only `/api/v1/files`, with a comment saying file
 * upload "has no method in this task's list" and that `upload_file` was "confirmed callable but no
 * captured response documents its shape". The method existed the whole time under a different
 * name; only its shape was unmeasured. Probed live:
 *
 *     POST mail.upload_attachment  {file_name, file_data: <base64>, is_private: 1}
 *       → 200 {file_url: "/private/files/…", file_name: "…"}
 *
 * `ComposeModal` no longer needs to disable its Attach button and drop zone through
 * `liveAvailability.ts`. The body shape and the private-only rule live in `lib/mailUpload.ts`.
 * ============================================================================================ */

/** The `File` record's docname for a stored url, or `''` when it cannot be read. */
async function fileDocnameFor(fileUrl: string): Promise<string> {
  const page = await getResource<{ name?: unknown }>('File', {
    fields: ['name'],
    filters: [['file_url', '=', fileUrl]],
    limit: 1,
  })
  return str(page.data[0]?.name)
}

export function useUploadFile() {
  return useMutation<UploadedFile, BosunApiError, File>({
    mutationFn: async (file) => {
      /*
        Read as a data URL and stripped, rather than hand-rolling base64 over an ArrayBuffer:
        `btoa(String.fromCharCode(...bytes))` blows the argument limit on a file of any size, and
        chunking it is a second implementation of something the platform already does correctly.
      */
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onerror = () => reject(new Error(`Could not read ${file.name}`))
        reader.onload = () => resolve(String(reader.result ?? ''))
        reader.readAsDataURL(file)
      })

      const wire = await callPost<WireUploadAttachment>(
        'mail',
        'upload_attachment',
        toUploadBody(file.name, dataUrl),
      )
      // The docname the SEND will look this up by — `upload_attachment` answers only the url,
      // and `send_email` 500s on one (2026-09-22; `D44` asks for `name` in the response).
      return withFileDocname(toUploadedFile(wire, file.size), fileDocnameFor)
    },
  })
}
